/**
 * Identity Provider Wrapper
 * 
 * This component wraps the application with the IdentityProvider from @workspace/identity-react
 * for standalone authentication outside of Evo native tabs.
 * 
 * The wrapper detects whether the app is running in standalone mode or inside an Evo tab,
 * and only applies the IdentityProvider when running standalone.
 */

import React from 'react';
import { IdentityProvider } from '@workspace/identity-react';
import { identityConfig } from '../config/identity';
import { isRunningInEvoTab } from '../utils/evoDetection';

interface IdentityProviderWrapperProps {
  children: React.ReactNode;
}

/**
 * Handle the signin callback from Identity
 * 
 * This callback is invoked after a successful signin redirect.
 * It can be used to restore application state or perform post-login actions.
 */
const handleSigninCallback = (): void => {
  // Clear any stored redirect state
  const returnUrl = sessionStorage.getItem('auth_return_url');
  
  // IMPORTANT: Don't redirect immediately - let the identity state update first
  // The LoginCallback component will handle the redirect when identity.isAuthenticated becomes true
  
  if (returnUrl) {
    sessionStorage.removeItem('auth_return_url');
  }
  
  // Store a flag to indicate successful callback processing
  sessionStorage.setItem('oidc_callback_processed', Date.now().toString());
};

/**
 * IdentityProviderWrapper component
 * 
 * Wraps children with IdentityProvider when running in standalone mode.
 * When running inside an Evo tab, children are rendered without the wrapper
 * as authentication is handled by the Evo framework.
 */
export const IdentityProviderWrapper: React.FC<IdentityProviderWrapperProps> = ({ children }) => {
  // Check if we're running inside an Evo tab
  const inEvoTab = isRunningInEvoTab();
  
  // If running in Evo tab, don't wrap with IdentityProvider
  // The app will use useWidgetIdentity from @workspace/utils-react instead
  if (inEvoTab) {
    return <>{children}</>;
  }
  
  // Running standalone - wrap with IdentityProvider for OIDC authentication
  return (
    <IdentityProvider
      authority={identityConfig.authority}
      clientId={identityConfig.clientId}
      redirectUri={identityConfig.redirectUri}
      scope={identityConfig.scope}
      workspaceUrl={identityConfig.workspaceUrl}
      signinFlow={identityConfig.signinFlow}
      onSigninCallback={handleSigninCallback}
    >
      {children}
    </IdentityProvider>
  );
};

export default IdentityProviderWrapper;
